import { useCallback, useSyncExternalStore } from 'react';

/**
 * Observa uma media query.
 *
 * Guarda-se contra `matchMedia` inexistente (jsdom não o implementa e há
 * webviews antigas sem ele): nesse caso devolve `false` em vez de quebrar.
 */
export function useMediaQuery(query: string): boolean {
  const subscribe = useCallback(
    (onChange: () => void) => {
      if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
        return () => {};
      }
      const list = window.matchMedia(query);
      if (typeof list.addEventListener === 'function') {
        list.addEventListener('change', onChange);
        return () => list.removeEventListener('change', onChange);
      }
      // Safari < 14 e webviews antigas: só existe a API depreciada.
      list.addListener(onChange);
      return () => list.removeListener(onChange);
    },
    [query],
  );

  const getSnapshot = useCallback(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
    return window.matchMedia(query).matches;
  }, [query]);

  // No servidor não há media queries: assume-se o caso móvel (o dela).
  const getServerSnapshot = useCallback(() => false, []);

  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
}

/** Atalhos usados pelas telas. O tablet da filha começa nos 768 px. */
export const useIsTablet = () => useMediaQuery('(min-width: 768px)');
export const usePrefersReducedMotion = () =>
  useMediaQuery('(prefers-reduced-motion: reduce)');
export const usePrefersDark = () => useMediaQuery('(prefers-color-scheme: dark)');
